/* eslint-disable */
import { useCallback, useLayoutEffect, useEffect, useRef, useState } from 'react';
import s from './devsBar.module.css';

// ============================================================
// useDevsFold — David's v6 fit(): ONE plan per open / close of the stage tray,
// decided up front from the controls' NATURAL widths (measured, never guessed).
// Fold order while the tray is open and the row is short of room:
//   f1 page name · f2 Sort + Views words · f3 Views + Sort + View into ⋯ ·
//   f4 Designer / Season / Factory into Filters · f5 search to an icon ·
//   f6 Filters word · f7 Stages word · f8 (last resort) the tray scrolls inside.
// Folds are cells with a measured --w; the fit only sets a level class on the
// root (f1…f8), so React never owns those classes. Nothing here changes what
// filters do — it is layout only. Elements are found by data-f, not by class.
// ============================================================

const FL = ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'nd', 'f8'];
// CLB-079 round 7: 'nd' = the New dev word folds to a "+" square (after f7, before the tray scrolls).
// Tray closed folds only when the row truly cannot fit (search at its 110px floor), in this order:
const LAD_OPEN = ['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'nd', 'f8'];
const LAD_SHUT = ['f6', 'f7', 'nd', 'f3', 'f4'];
const TM = 220;
const mqPhone = () => typeof window !== 'undefined' && window.matchMedia('(max-width:760px)').matches;
const mqWide = () => typeof window !== 'undefined' && window.matchMedia('(min-width:1900px)').matches;
const mqReduced = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion:reduce)').matches;

export function useDevsFold({ rootRef, stOpen, setStOpen }) {
  const st = useRef({ lv: new Set(), animT: 0, afterAnim: null, phs: false });
  const [lv, setLv] = useState('');

  const root = () => rootRef.current;
  const q = (k) => root() && root().querySelector(`[data-f="${k}"]`);
  const on = (e) => !!e && e.getClientRects().length > 0;
  const bw = (e) => (on(e) ? e.getBoundingClientRect().width : 0);
  const nat = (e) => (on(e) && e.firstElementChild ? Math.ceil(e.firstElementChild.getBoundingClientRect().width - 0.01) : 0);

  const levelClasses = (set) => { const r = root(); if (!r) return; FL.forEach((c) => r.classList.toggle(s[c], set.has(c))); };
  const key = (set) => [...set].join(' ');

  const measure = () => {
    const bar = q('bar'); if (!bar) return null;
    const cs = getComputedStyle(bar), G = parseFloat(cs.columnGap) || 6;
    const W = bar.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const idn = q('idn'), idl = q('idl'), stt = q('stt'), lbw = q('lbw'), sgb = q('sgb'), ddw = q('ddw'), fb = q('fb'), fwl = q('fwl'), dkw = q('dkw'), srl = q('srl'), vwl = q('vwl'), mo = q('more');
    const nd = q('nd'), ndl = q('ndw'), ndc = on(nd) ? getComputedStyle(nd) : null;
    const srlN = nat(srl), vwlN = nat(vwl);
    const mini = q('mini');
    return {
      W, G, items: Array.from(bar.children).filter(on).length,
      idnOn: on(idn), idnBase: bw(idn) - bw(idl), idl: nat(idl),
      sttBase: bw(stt) - bw(lbw), lbw: nat(lbw),
      tray: Math.ceil(bw(sgb)) + G,
      ddw: nat(ddw),
      fbBase: bw(fb) - bw(fwl) - (on(fb) ? parseFloat(getComputedStyle(fb).paddingLeft) : 0), fwl: nat(fwl),
      dkOn: on(dkw), dkw: on(dkw) ? Math.ceil(nat(dkw) - bw(srl) + srlN - bw(vwl) + vwlN) : 0, srl: srlN, vwl: vwlN,
      more: bw(mo), mini: bw(mini),
      ndOn: !!ndc, ndl: nat(ndl),
      ndBase: ndc ? bw(nd) - bw(ndl) - parseFloat(ndc.paddingLeft) - parseFloat(ndc.paddingRight) : 0,
      ndM: ndc ? parseFloat(ndc.marginLeft) + parseFloat(ndc.marginRight) : 0,
    };
  };

  const plan = (open) => {
    const M = measure(); if (!M) return null;
    const G = M.G, smin = open ? Math.round(Math.min(260, Math.max(200, M.W * 0.125))) : 110;
    const tot = (f, tray) => (M.items - 1) * G + M.more + M.mini + tray - G
      + (M.idnOn ? M.idnBase + (f.has('f1') ? 0 : M.idl) : 0)
      + (f.has('f5') ? 32 : smin)
      + M.sttBase + (f.has('f7') ? 0 : M.lbw)
      + (f.has('f4') ? 0 : M.ddw) - G
      + M.fbBase + (f.has('f6') ? 9 : 11) + (f.has('f6') ? 0 : M.fwl)
      + (M.dkOn ? (f.has('f3') ? 0 : f.has('f2') ? M.dkw - M.srl - M.vwl : M.dkw) - G : 0)
      + (M.ndOn ? M.ndM + M.ndBase + (f.has('nd') ? 16 : 22) + (f.has('nd') ? 0 : M.ndl) : 0);
    const lad = open ? LAD_OPEN : LAD_SHUT, set = new Set(), trayW = open ? M.tray : 0;
    let i = 0, tw = M.tray;
    while (i < lad.length && tot(set, trayW) > M.W - 1) set.add(lad[i++]);
    if (open && set.has('f8')) tw = Math.max(140, Math.floor(M.W - 1 - tot(set, G) + G));
    const av = M.W - (tot(set, open ? tw : 0) - (set.has('f5') ? 32 : smin)), mx = mqWide() ? 560 : 420;
    const sw = set.has('f5') ? 32 : av <= 240 ? Math.max(110, av) : Math.min(mx, 240 + (av - 240) / 2);
    return { M, set, tw, sw, open };
  };

  const setW = (P) => {
    const M = P.M, put = (k, v) => { const e = q(k); if (e && v) e.style.setProperty('--w', v + 'px'); };
    put('ndw', M.ndl); put('idl', M.idl); put('srl', M.srl); put('vwl', M.vwl); put('ddw', M.ddw); put('fwl', M.fwl); put('lbw', M.lbw);
    put('dkw', M.dkOn ? (P.set.has('f2') ? M.dkw - M.srl - M.vwl : M.dkw) : 0);
    if (P.open) { const y = q('sty'); if (y) y.style.setProperty('--tw', P.tw + 'px'); }
    const ph = P.sw < 340;
    if (ph !== st.current.phs) { st.current.phs = ph; const i = q('srch') && q('srch').querySelector('input'); if (i) i.placeholder = ph ? 'Search devs…' : 'Search dev no., designer, factory, shape…'; }
  };

  const endAnim = () => {
    const c = st.current; clearTimeout(c.animT); c.animT = 0;
    const r = root(); if (!r) return;
    r.classList.remove(s.anim);
    const sr = q('srch'); if (sr && sr.hasAttribute('style')) sr.removeAttribute('style');
    if (!c.lv.has('f5')) r.classList.remove(s.sI);
    if (c.afterAnim) { const k = c.afterAnim; c.afterAnim = null; focusF(k); }
  };
  const focusF = (k) => { const el = k && root() && root().querySelector(`[data-fk="${CSS.escape(k)}"]`); if (el) el.focus({ preventScroll: true }); };

  const fitRef = useRef(null);
  const fit = useCallback(() => {
    const c = st.current, r = root();
    if (!r) return;
    if (c.animT) endAnim();
    if (mqPhone() || !q('bar')) { const e0 = new Set(); levelClasses(e0); c.lv = e0; r.classList.remove(s.sI); setLv((p) => (p === '' ? p : '')); return; }
    const P = plan(stOpen); if (!P) return;
    setW(P); levelClasses(P.set); c.lv = P.set; r.classList.toggle(s.sI, P.set.has('f5'));
    const k = key(P.set); setLv((p) => (p === k ? p : k));
  }); // eslint-disable-line

  // The tray move: ONE plan, applied in one go — width, folds, tray and search glide on the same clock.
  const openTray = useCallback((open, focus) => {
    const c = st.current, r = root(), t = q('stt'), y = q('sty');
    if (!r || !t || !y || mqPhone()) { setStOpen(open); return; }
    const P = plan(open); if (!P) { setStOpen(open); return; }
    const sr = q('srch'), wasF = r.classList.contains(s.f5), willF = P.set.has('f5'), move = !!sr && wasF !== willF && !mqReduced();
    if (c.animT && (move || (sr && sr.hasAttribute('style')))) endAnim();
    let w1 = 0;
    if (move) {
      if (!willF) { const was = y.classList.contains(s.open); setW(P); levelClasses(P.set); y.classList.toggle(s.open, open); w1 = sr.getBoundingClientRect().width; levelClasses(c.lv); y.classList.toggle(s.open, was); }
      const w0 = sr.getBoundingClientRect().width;
      sr.style.flex = `0 0 ${w0}px`; sr.style.minWidth = '0'; sr.style.maxWidth = 'none';
      void sr.offsetWidth;
    }
    r.classList.add(s.anim);
    setW(P); levelClasses(P.set); c.lv = P.set; y.classList.toggle(s.open, open); t.classList.toggle(s.open, open);
    if (willF) r.classList.add(s.sI);
    if (move) sr.style.flexBasis = (willF ? 32 : w1) + 'px';
    clearTimeout(c.animT);
    if (mqReduced()) { c.animT = 0; endAnim(); } else c.animT = setTimeout(endAnim, TM + 80);
    setLv(key(P.set));
    setStOpen(open);
    if (focus) c.afterAnim = null, focusF(focus);
  }); // eslint-disable-line

  fitRef.current = fit;
  useLayoutEffect(() => { fit(); });
  useEffect(() => {
    let raf = 0; const rp = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => fitRef.current && fitRef.current()); };
    let lastW = 0;
    const ro = typeof ResizeObserver !== 'undefined' && root() ? new ResizeObserver(() => { const w = root().clientWidth; if (w !== lastW) { lastW = w; rp(); } }) : null;
    ro && ro.observe(root());
    window.addEventListener('resize', rp);
    if (document.fonts) { document.fonts.ready && document.fonts.ready.then(rp); document.fonts.addEventListener && document.fonts.addEventListener('loadingdone', rp); }
    return () => { cancelAnimationFrame(raf); ro && ro.disconnect(); window.removeEventListener('resize', rp); document.fonts && document.fonts.removeEventListener && document.fonts.removeEventListener('loadingdone', rp); clearTimeout(st.current.animT); };
  }, []); // eslint-disable-line

  return { openTray, lv, focusSearch: () => { const c = st.current; if (c.animT && root().classList.contains(s.sI)) { c.afterAnim = 'q'; return; } focusF('q'); } };
}
